import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AutomationRun } from './automationRules';

import { api } from '../utils/api';
import { ApiResponse } from '../types/api';

export interface IncidentLinkedProject {
    id: number;
    name: string;
}

export interface IncidentSecondary {
    id: number;
    project_id: number;
    project_name: string;
}

/** The incident whose agent is monitoring this fire, when it is not this one. */
export interface IncidentCoveredBy {
    id: number;
    project_id: number;
    project_name: string;
}

/**
 * A person's ruling on the automatic same-fire judgement.
 *
 * Whether two incidents are one fire is decided from mapped shapes and the
 * contributing sensor's resolution — a good guess, never more than that. When
 * it is wrong the consequence is real (a fire folded into another's
 * monitoring shows no agent at all), so a human can settle it either way.
 */
export type IncidentLinkOverride = '' | 'different_fire' | 'same_fire';

/**
 * Derived lifecycle word, computed server-side from the incident's fields so
 * every surface (map, list, modal) says the same thing. Order = how much a
 * reader should care.
 */
export type IncidentStatus =
    | 'escalated_rekindle'
    | 'active'
    | 'silent_rekindle'
    | 'stand_down'
    | 'quiet'
    | 'closed_by_human'
    | 'closed_by_agent';

export const OPEN_INCIDENT_STATUSES: IncidentStatus[] = [
    'escalated_rekindle',
    'active',
    'silent_rekindle',
    'stand_down',
    'quiet',
];
export const CLOSED_INCIDENT_STATUSES: IncidentStatus[] = [
    'closed_by_human',
    'closed_by_agent',
];

export interface IncidentSummary {
    id: number;
    /** The kind of the detection that opened the incident. */
    kind: string;
    status: IncidentStatus;
    /** One sentence: who did what, where the fire is relative to what came
     *  before, who knows. Server-derived. */
    headline: string;
    /** Convex hull of the detection footprint — for EVERY incident, closed
     *  ones included (a Polygon for 3+ points, else LineString / Point). */
    hull: GeoJSON.Geometry;
    /** [lon, lat] centre of the footprint. */
    centroid: [number, number];
    /** Predecessor chain, nearest first: the incidents this one rekindled. */
    chain: number[];
    /** Incidents that rekindled THIS one. */
    rekindles: number[];
    /** Rekindles only: metres from the nearest opening detection to the
     *  predecessor chain's footprint hull, and the radius it had to be
     *  inside to link at all. */
    link_distance_m: number | null;
    link_radius_m: number | null;
    /** A person's reason from the close dialog, or the agent's close-out
     *  summary. Empty when neither gave one. */
    close_reason: string;
    /** Every detection kind absorbed so far, opening kind first: hotspot,
     *  ignition and burnt-area detections at one place are one incident. */
    kinds: string[];
    /** One fire, several projects: set when this incident is a secondary
     *  linked to the incident on a neighbouring project that first tracked
     *  the fire (that one holds the whole footprint and the agent, if any). */
    primary_id: number | null;
    primary_project: IncidentLinkedProject | null;
    /** Incidents on neighbouring projects linked to this one (primary only). */
    secondaries: IncidentSecondary[];
    /** The fire's mapped outline, where detections carried one (a customer
     *  burnt-area perimeter). `hull` is drawn from the detection SAMPLE and
     *  is not the fire's edge; this is. Null when the incident is known only
     *  from points. */
    area: GeoJSON.Geometry | null;
    /** Monitored under another incident: the fire already has an agent
     *  running there, so this row is NOT unmonitored even with no session of
     *  its own. Without this the card could only show a blank. */
    covered_by: IncidentCoveredBy | null;
    /** '' = the automatic same-fire judgement, else a person's ruling. */
    link_override: IncidentLinkOverride;
    link_override_by: string | null;
    /** Everyone this incident has been announced to, first-told first —
     *  incidents are project-level, so one fire can notify many users. */
    users: string[];
    detections: number;
    first_detected_at: string;
    last_detected_at: string;
    active: boolean;
    /** Set when a user manually closed the incident. A closed incident stops
     *  absorbing detections — a still-burning fire re-alerts as a new one. */
    closed_at: string | null;
    closed_by: string | null;
    title: string | null;
    /** Set when this incident is a re-detection at an incident closed within
     *  the rekindle window — it is linked to that predecessor rather than
     *  being a brand-new fire.
     *
     *  Since 2026-09-19 these are only created for a fire found ALREADY
     *  BEYOND the closed footprint on its first pass back (a genuine escape,
     *  and therefore news). Anything else reopens the original incident —
     *  see `reopened_at`. Older silent rekindles still exist and still
     *  render. */
    predecessor_id: number | null;
    /** A rekindle nobody has been told about and no agent monitors: every
     *  detection so far sits inside the closed fire's detection footprint.
     *  Legacy — no new ones are created. */
    silent: boolean;
    /** This incident was closed and came back: detections reappeared at the
     *  same fire, so it was reopened and handed back to its agent rather
     *  than spawning a second, invisible incident beside it. */
    reopened_at: string | null;
    reopen_count: number;
    /** Only present on rekindles (predecessor_id set). */
    rekindle?: IncidentRekindle;
    /** Commander stand-down: re-detections here stay silent until then. */
    stand_down_until?: string | null;
    /** [lon, lat] of every detection absorbed so far — active incidents
     *  only (null otherwise). Lets the project map label the hotspot cluster
     *  a fire belongs to with its incident number. */
    footprint?: [number, number][] | null;
}

export interface IncidentRekindle {
    inside_prior_footprint: number;
    outside_prior_footprint: number;
    max_outside_m: number | null;
    outside_pass_count: number;
    escalated_at: string | null;
    escalation_reason: 'outside_footprint' | 'agency_warning' | null;
}

export interface IncidentDetection {
    id: number;
    observed_at: string;
    lon: number;
    lat: number;
}

export interface IncidentNotification {
    at: string;
    title: string;
    body: string;
    user: string;
    deliveries: { channel: string; status: string }[];
}

export interface IncidentSnooze {
    at: string;
    user: string;
    action: 'snooze' | 'resume';
    hours: number | null;
    until: string | null;
}

export interface IncidentDetail {
    incident: IncidentSummary;
    /** Full summaries of the predecessor chain (nearest first) and of the
     *  incidents that rekindled this one, so the modal can draw and link
     *  them without more requests. */
    chain: IncidentSummary[];
    rekindles: IncidentSummary[];
    footprint: { lon: number; lat: number }[];
    detections: IncidentDetection[];
    /** Raw detection rows are pruned after the 72 h retention window; counts
     *  and the footprint survive on the incident itself. */
    detections_pruned: boolean;
    /** Every announcement digest, oldest first: the first alert plus any
     *  later subscriber the fire spread towards. */
    notifications: IncidentNotification[];
    snoozes: IncidentSnooze[];
    /** Detection-automation firings for this incident (rule-driven
     *  simulations and their GIS publishes), newest first. */
    automation_runs: AutomationRun[];
}

/** Default history window for the incident list (closed/ended incidents
 *  older than this are trimmed; open ones are always listed). */
export const DEFAULT_INCIDENT_DAYS = 30;

/**
 * The project's incidents, one shared 60 s-polled cache for the map layer,
 * the Incidents tab and the popovers. `days` trims closed history (0 = all).
 */
export const useProjectIncidents = (
    projectId: string | number | undefined,
    options: { days?: number } = {}
) => {
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    const days = options.days ?? DEFAULT_INCIDENT_DAYS;
    return useQuery({
        queryKey: ['project', idKey, 'incidents', 'list', days],
        queryFn: () =>
            api.request<ApiResponse<{ incidents: IncidentSummary[]; days: number }>>(
                `/api/projects/${idKey}/incidents?days=${days}`
            ),
        enabled: !!idKey,
        refetchOnWindowFocus: false,
        refetchInterval: 60_000,
    });
};

export const useIncidentDetail = (
    projectId: string | undefined,
    incidentId: string | undefined
) =>
    useQuery({
        queryKey: ['project', projectId, 'incidents', incidentId],
        queryFn: () =>
            api.request<ApiResponse<IncidentDetail>>(
                `/api/projects/${projectId}/incidents/${incidentId}`
            ),
        enabled: !!projectId && !!incidentId,
        refetchOnWindowFocus: false,
        refetchInterval: 60_000,
    });

export const useCloseIncident = (projectId: string | number | undefined) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    return useMutation({
        // quietHours: optional stand-down — re-detections at this fire stay
        // silent for that long (only a Watch-and-Act+ agency warning, or a
        // human, can escalate a rekindle while it runs).
        mutationFn: ({
            incidentId,
            quietHours,
            reason,
        }: {
            incidentId: number;
            quietHours?: number;
            /** The closer's own words, shown wherever the incident is. */
            reason?: string;
        }) =>
            api.post<ApiResponse<{ incident: IncidentSummary }>>(
                `/api/projects/${idKey}/incidents/${incidentId}/close`,
                {
                    ...(quietHours ? { quiet_hours: quietHours } : {}),
                    ...(reason?.trim() ? { reason: reason.trim() } : {}),
                }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['project', idKey, 'incidents'],
            });
            // The listing's open-incident badge/highlight reads a count that
            // just changed.
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
};

/**
 * Overrule the automatic same-fire judgement.
 *
 * `different_fire` unlinks this incident from the fire it was folded into and,
 * on an auto-monitored project, starts its own agent immediately.
 * `same_fire` (with `coveredBy`) ties it to an incident whose agent is
 * already on the fire. `''` hands it back to the automatic judgement.
 */
export const useSetIncidentLink = (projectId: string | number | undefined) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    return useMutation({
        mutationFn: ({
            incidentId,
            override,
            coveredBy,
        }: {
            incidentId: number;
            override: IncidentLinkOverride;
            coveredBy?: number;
        }) =>
            api.post<
                ApiResponse<{
                    incident: IncidentSummary;
                    agent_started: boolean;
                }>
            >(`/api/projects/${idKey}/incidents/${incidentId}/link`, {
                override: override === '' ? null : override,
                ...(coveredBy ? { covered_by: coveredBy } : {}),
            }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['project', idKey, 'incidents'],
            });
            queryClient.invalidateQueries({
                queryKey: ['project', idKey, 'incidents', variables.incidentId],
            });
            // An override can start an agent, which the session panel polls.
            queryClient.invalidateQueries({
                queryKey: ['project', idKey, 'agent-sessions'],
            });
        },
    });
};

/**
 * Manually create an incident from a detection on the map — the escape hatch
 * for fires that predate the project (or predate detections being enabled),
 * which the notification pipeline's freshness gate will never turn into an
 * incident on its own. Nobody is notified by creation; subscribers are
 * announced by the next fresh detection that merges in.
 */
export const useCreateIncidentFromDetection = (
    projectId: string | number | undefined
) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    return useMutation({
        mutationFn: async (observationId: number) => {
            const res = await api.post<
                ApiResponse<{ created: boolean; incident: IncidentSummary }>
            >(`/api/projects/${idKey}/incidents`, {
                observation_id: observationId,
            });
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['project', idKey, 'incidents'],
            });
            // Open-incident badge on the listing.
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
};
